/**
 * useCart — localStorage-backed shopping cart for /website purchases.
 *
 * Items are one of two shapes:
 *   { type: "subscribe", plan: "basis"|"plus", label, price_eur }
 *   { type: "domain",   domain: "yourbrand.nl", ext: "nl", price_eur }
 *
 * The cart is intentionally minimal — just enough to hand items off to the
 * existing website-domains Stripe checkout endpoints one at a time (Stripe
 * subscriptions cannot combine different billing intervals, so we process
 * items sequentially rather than as a single Session).
 */
import { useCallback, useEffect, useState } from "react";

const KEY = "zynthoro_wd_cart_v1";

const read = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

const write = (items) => {
  try { localStorage.setItem(KEY, JSON.stringify(items)); } catch { /* ignore */ }
};

// Cross-tab / cross-hook sync so multiple components stay consistent.
const listeners = new Set();
const notify = () => listeners.forEach((cb) => { try { cb(); } catch { /* ignore */ } });

export function useCart() {
  const [items, setItems] = useState(read);

  useEffect(() => {
    const sync = () => setItems(read());
    listeners.add(sync);
    window.addEventListener("storage", sync);
    return () => {
      listeners.delete(sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const persist = useCallback((next) => {
    write(next);
    setItems(next);
    notify();
  }, []);

  const idOf = (i) => {
    if (i.type === "subscribe") return `plan:${i.plan}`;
    if (i.type === "kickstart") return `kickstart:${i.plan}`;
    return `domain:${i.domain}`;
  };

  const add = useCallback((item) => {
    const cur = read();
    const id = idOf(item);
    if (cur.some((x) => idOf(x) === id)) return { added: false, reason: "duplicate" };
    persist([...cur, item]);
    return { added: true };
  }, [persist]);

  const remove = useCallback((item) => {
    const id = idOf(item);
    persist(read().filter((x) => idOf(x) !== id));
  }, [persist]);

  const clear = useCallback(() => persist([]), [persist]);

  const total = items.reduce((s, i) => s + Number(i.price_eur || 0), 0);

  return { items, add, remove, clear, total };
}
